import {orderBy} from 'lodash'
import clsx from 'clsx'
import {React, useContext} from 'external/react'
import {groupQuestionStandards} from 'components/standards-selector/domain/question-summary.cjs'
import {resolveItemEdition} from 'components/standards-selector/domain/editions.cjs'
import {Link} from 'react-router-dom'
import {makeStyles, Accordion, AccordionSummary, AccordionDetails} from 'external/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'


import {TeacherContext} from 'teacher/context'

import {isUUID} from 'helpers/utils'
import AddStandards from 'teacher/components/add-standards'
import Standard from './standard'

const useStyles = makeStyles(standardStyles)
const useAccordionStyles = makeStyles(accordionStyles)
const useAccordionDetailsStyles = makeStyles(accordionDetailsStyles)
const useAccordionSummaryStyles = makeStyles(accordionSummaryStyles)

export default function AddStandard ({onStandardsChange, question}) {

  const classes = useStyles()
  const accordionClasses = useAccordionStyles()
  const accordionDetailsClasses = useAccordionDetailsStyles()
  const accordionSummaryClasses = useAccordionSummaryStyles()
  const context = useContext(TeacherContext)

  const {translate, link, updateState, assessment, operation, quizAssessment, quizQuestionStandards} = context

  const deleteStandard = (id) => {
    onStandardsChange(standards.filter(s => s.id != id))
  }

  const backPath = link({operation: undefined})

  const {standards = []} = question.properties || {}
  const standardIndex = quizQuestionStandards.findIndex(q => q.id == question.id) || -1

  // Search through the standards for the closest question with a specified standard
  const quizStandards = orderBy(quizQuestionStandards
    .map((q,i) => ({...q, relevance: Math.abs(standardIndex - i)}))
    .filter(q => q.standards.length > 0), ['relevance'])
  const [firstQuizStandard = {}] = quizStandards
  const [firstStandard] = firstQuizStandard.standards || []

  const standardLinkQuery = firstStandard
    ? {subject: firstStandard.subject, grade: firstStandard.grade, standard: firstStandard.standard, standard_set: resolveItemEdition(firstStandard)}
    : {subject: quizAssessment.subject, grade: quizAssessment.course ? quizAssessment.course.substring(quizAssessment.course.length - 3) : undefined}

  const standardLink = link({operation: 'add-standards'}, undefined, {query: standardLinkQuery})
  
  function handleOpenStandardsClick (e) {
    e.stopPropagation()
    const selected = standards.reduce((acc, standard) => ({...acc, [standard.id]: standard}), {})
    const questionStandards = quizStandards.reduce((acc, {index, sectionIndex, standards}) => {
      standards.forEach(({id}) => {
        if (acc[id]) acc[id].push({index, sectionIndex})
        else acc[id] = [{index, sectionIndex}]
      })
      return acc
    }, {})
    updateState({standardsContext:{selectStandards: {
      selected: {$set: selected},
      questionStandards: {$set: questionStandards}
    }}})
  }

  const editionGroups = groupQuestionStandards(standards)

  return (<>
      <AddStandards
        backPath={backPath}
        onSaveStandards={onStandardsChange}
        open={operation == 'add-standards'}
      />
      <Accordion
        defaultExpanded={true}
        square={true}
        classes={accordionClasses}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-label="Expand"
          aria-controls="expand-standards"
          id="expand-standards"
          classes={accordionSummaryClasses}
        >
          <span className={classes.addStandardLabel} >{translate("standards")}:</span>
          <Link className={classes.addStandard} to={standardLink} onFocus={e => e.stopPropagation()} onClick={handleOpenStandardsClick}>+</Link>
        </AccordionSummary>
        <AccordionDetails classes={accordionDetailsClasses}>
          {editionGroups.length > 0 && (
            <div className={clsx(classes.wrapper, classes.content)}>
              {editionGroups.map(group => (
                <div key={group.id}>
                  <div className={classes.editionHeading}>{translate('standards')} · {group.year}</div>
                  {group.items.map((standard, index) => (
                    <Standard
                      index={index}
                      key={`${group.id}:${standard.id}`}
                      deleteStandard={deleteStandard}
                      {...standard}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </AccordionDetails>
      </Accordion>
  </>
  )
}

function accordionStyles() {
  return {
    root: {
      border: 'none',
      boxShadow: 'none',
      borderBottom: 'solid 1px #e2ecf2'
    }
  }
}

function accordionSummaryStyles () {
  return {
    root: {
      padding: 0
    }
  }
}

function accordionDetailsStyles () {
  return {
    root: {
      padding: '8px 0'
    }
  }
}

function standardStyles(theme) {
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
    },
    item: {
      display: 'flex',
      flexDirection: 'column'
    },
    head: {
      color: '#868c9c',
      padding: '8px 20px 15px 20px',
      borderBottom: 'solid 1px #e2ecf2'
    },
    addStandardLabel: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      color: '#69738c',
      fontWeight: 'bold',
      flexGrow: 1,
    },
    addStandard: {
      borderRadius: 4,
      textDecoration: 'none',
      color: '#a5b9de',
      textAlign: 'center',
      fontSize: '15pt',
      backgroundColor: '#f0f7ff',
      border: 'solid 1px #a5b9de',
      padding: '4px 8px',
      "&:hover": {
        transform: 'scale(1.01)',
        fontWeight: 'bold'
      }
    },
    editionHeading: {
      color: '#69738c',
      fontWeight: 'bold',
      padding: '12px 20px 6px',
      borderBottom: 'solid 1px #e2ecf2'
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      padding: '0 0 0 0',
    }
  }
}
